/**
 * SeenByMarks — who has read this message (2026-09-08, PR-3; inline + white
 * 2026-09-09). One small circle per reader with their initial, at the head
 * of the meta line beside the reaction chips (MetaLeading) — never a row of
 * its own. The full list is the tooltip and the accessible label. Pure
 * presentational — the host computes the readers from receipts
 * (services/groupThreads/seenBy.ts).
 *
 * WHO (2026-09-19): the names lived in `title` alone — never rendered by a
 * touch browser — so on a phone the initials said that somebody had read it
 * and refused to say who. With `onShow` the group becomes a real button onto
 * the message's activity surface, opened on its `seen` tab. Absent ⇒ this
 * file behaves exactly as it did.
 *
 * WHITE (Miles, 2026-09-09: "all these elements need to be consistently
 * white of course" — clarified: the seen-by identifiers): every initial is
 * white on a filled circle — the assistant on the dark gold, a human on the
 * secondary text colour — with a white hairline and a soft dark ring so the
 * mark reads on the gold user bubble and the soft-white Prest0n bubble
 * alike. Tokens only, plus #fff (check-design-tokens.mjs).
 */
import React from 'react';
import styled from 'styled-components';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';

export interface SeenByReader {
  id: string;
  label: string;
  glyph: string;
  /** Prest0n's receipt renders with the assistant mark. */
  assistant?: boolean;
}

export interface SeenByMarksProps {
  readers: ReadonlyArray<SeenByReader>;
  /** Opens the activity surface on its `seen` tab, anchored on the marks. Absent ⇒ a plain readout. */
  onShow?: (anchorEl: HTMLElement) => void;
}

export const SeenByGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  line-height: 1;
  user-select: none;
`;

/** The same marks, worn as the door to "who has read this". */
export const SeenByGroupButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  line-height: 1;
  user-select: none;
  padding: 1px 2px;
  border: none;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 1px;
  }
`;

export const SeenByDot = styled.span<{ $assistant?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0;
  color: #fff;
  background: ${(p) => (p.$assistant ? 'var(--brand-gold-text)' : 'var(--brand-text-secondary)')};
  border: 1px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.14);
`;

export function seenByLabel(readers: ReadonlyArray<SeenByReader>, lang: 'en' | 'es'): string {
  const names = readers.map((r) => r.label).filter(Boolean);
  if (names.length === 0) return '';
  return lang === 'es' ? `visto por ${names.join(', ')}` : `seen by ${names.join(', ')}`;
}

export function SeenByMarks({ readers, onShow }: SeenByMarksProps): React.ReactElement | null {
  if (!readers || readers.length === 0) return null;
  const lang = getUserUiLanguage() === 'es' ? 'es' : 'en';
  const label = seenByLabel(readers, lang);
  const dots = readers.map((r) => (
    <SeenByDot key={r.id} data-seen-by-reader={r.id} aria-hidden="true" $assistant={r.assistant}>
      {r.glyph}
    </SeenByDot>
  ));
  const shared = {
    'aria-label': label,
    title: label,
    'data-seen-by': readers.map((r) => r.id).join(','),
  };
  if (onShow) {
    return (
      <SeenByGroupButton
        type="button"
        data-seen-by-show=""
        {...shared}
        onClick={(e) => { e.stopPropagation(); onShow(e.currentTarget as HTMLElement); }}
      >
        {dots}
      </SeenByGroupButton>
    );
  }
  return (
    <SeenByGroup role="img" {...shared}>
      {dots}
    </SeenByGroup>
  );
}

export default SeenByMarks;
